import { clamp } from "../../core/math";
import { smoothSet } from "../envelope";

export interface ReverbParams { decaySec: number; returnLevel: number; }
export const DEFAULT_REVERB_PARAMS: ReverbParams = { decaySec: 2.2, returnLevel: 0.8 };

/**
 * Convolution reverb with a procedurally generated impulse response
 * (exponentially decaying stereo noise) — no third-party IR files.
 */
export class ReverbEffect {
  readonly input: GainNode;
  readonly output: GainNode;
  private convolver: ConvolverNode;
  private _params: ReverbParams;

  constructor(private ctx: BaseAudioContext, params: Partial<ReverbParams> = {}) {
    this._params = { ...DEFAULT_REVERB_PARAMS, ...params };
    this.input = ctx.createGain();
    this.convolver = ctx.createConvolver();
    this.output = ctx.createGain();
    this.output.gain.value = this._params.returnLevel;
    this.convolver.buffer = generateImpulse(ctx, this._params.decaySec);
    this.input.connect(this.convolver).connect(this.output);
  }

  get params(): ReverbParams { return this._params; }

  setParams(p: Partial<ReverbParams>): void {
    const prev = this._params;
    this._params = { ...prev, ...p };
    if (p.returnLevel !== undefined) smoothSet(this.output.gain, this._params.returnLevel, this.ctx);
    if (p.decaySec !== undefined && p.decaySec !== prev.decaySec) {
      this.convolver.buffer = generateImpulse(this.ctx, this._params.decaySec);
    }
  }

  dispose(): void {
    this.input.disconnect();
    this.convolver.disconnect();
    this.output.disconnect();
  }
}

export function generateImpulse(ctx: BaseAudioContext, decaySec: number): AudioBuffer {
  const d = clamp(decaySec, 0.1, 10);
  const len = Math.floor(ctx.sampleRate * d);
  const buf = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const data = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) {
      // ~60 dB decay over d seconds.
      data[i] = (Math.random() * 2 - 1) * Math.exp((-6.9 * i) / len);
    }
  }
  return buf;
}
